/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class ContentFactory
 * @hideconstructor
 * @classdesc API class that is used to generate content specs and create them
 */
define([
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../../canvas/Content',
	'../../../../../dashboard/layout/LayoutHelper',
	'../../../../../dashboard/glass/util/InstrumentationUtil',
	'../../../../../lib/@waca/dashboard-common/dist/api/ContentProviderAPI'
], (APIFactory, Content, LayoutHelper, InstrumentationUtil, ContentProviderAPI) => {
	class BaseContentProvider {
		constructor(options) {
			this.dashboardApi = options.features['API'];
			options.features['ContentFactory'].registerProvider(this);
		}

		getAPI() {
			return APIFactory.createAPI(this, [ContentProviderAPI]);
		}

		getTypeList() {
			const types = [null, undefined, ''];
			if (!this._contentTypes) {
				this._contentTypes = this.dashboardApi.getFeature('ContentTypeRegistry');
			}
			return  this._contentTypes ? this._contentTypes.getRegisteredTypes().concat(types): types;
		}

		addModel(options, { canvas, boardModel, canvasContent }, transactionToken) {
			const addOptions = {
				parentId: options.containerId,
				insertBefore: options.insertBefore,
				copyPaste: options.copyPaste,
				model: options.spec,
				modelIdsValid: options.modelIdsValid === true,
				layoutProperties: {
					style: options.spec.style || {}
				}
			};
			if (options.properties) {
				['left', 'top', 'height', 'width'].forEach(prop => {
					if (options.properties[prop] !== undefined){
						addOptions.layoutProperties.style[prop] = options.properties[prop];
						delete options.properties[prop];
					}
				});
			}
			LayoutHelper.setPreferredAddOptions(addOptions, boardModel.layout, this.dashboardApi);
			if (!addOptions.parentId && !canvasContent.getFeature('ContentViewDOM')) {
				addOptions.parentId = canvasContent.getId();
			}
			const isLayout = canvas.isLayoutType(options.spec.type);
			const contentRegistry = this.dashboardApi.getFeature('ContentTypeRegistry');
			const isTypeRegistered = contentRegistry ? contentRegistry.isTypeRegistered(options.spec.type) : false;

			let _add;
			if (options.spec.type === 'widget'){
				// this is the case we are adding a layout of type widget
				// it will follow the new structure where the widget model is under the features secfion
				_add = this._addContent.bind(this);
			} else if (!isLayout && ( options.spec.widgets || options.spec.layout && options.spec.layout.items)) {
				_add = this._addFragment.bind(this);
			} else if (!isLayout) {
				if (isTypeRegistered) {
					_add = this._addContent.bind(this);
				} else {
					_add = this._addWidget.bind(this);
				}
			} else {
				_add = this._addLayout.bind(this);
			}

			const returnValue = _add(addOptions, boardModel, {
				undoRedoTransactionId: transactionToken.transactionId,
				transactionToken: transactionToken,
				skipUndoRedo: true
			});
			return returnValue && returnValue.then ? returnValue : Promise.resolve(returnValue);
		}

		createContent(options) {
			return new Content(options);
		}

		_addWidget(addOptions, boardModel,  options) {
			this._track(addOptions);
			return boardModel.addWidget(addOptions, null, options)
				.then((payload) => {
					const parameter = payload && payload.value && payload.value.parameter;
					return parameter && parameter.model && parameter.model.id;
				});
		}

		_addFragment(addOptions, boardModel, options) {
			const payload = boardModel.addFragment(addOptions, null, options);
			const parameter = payload && payload.value && payload.value.parameter;
			return parameter && parameter.model && parameter.model.layout && parameter.model.layout.id;
		}

		_addContent(addOptions, boardModel,  options) {
			this._track(addOptions);
			return boardModel.addContent(addOptions, null, options);
		}

		_addLayout(addOptions, boardModel, options) {
			const payload = boardModel.addLayouts({ addLayoutArray: [addOptions] }, null, options);
			return payload.value.parameter.addLayoutArray[0].model.id;
		}

		_track (options = {}) {
			const model = options.model;
			if (model) {
				InstrumentationUtil.trackWidget('created', this.dashboardApi, {
					name: model.name,
					type: model.type,
					data: model.data,
					visId: model.visId
				});
			}
		}

	}
	return BaseContentProvider;
});
